import React, { useState, useMemo, useCallback } from 'react';
import { Box, Text, VStack, HStack } from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { PaginationControls } from 'src/components/molecules/PaginationControls';

type Campaign = {
  campaignId: string;
  campaignName: string;
  groupId: string | null;
  groupName: string | null;
};

type CampaignSelectListProps = {
  campaigns: Campaign[];
  selectedCampaigns: Record<string, boolean>;
  onChange: (selections: Record<string, boolean>) => void;
  isDisabled?: boolean;
  emptyMessage?: string;
};

export const CampaignSelectList: React.FC<CampaignSelectListProps> = ({
  campaigns,
  selectedCampaigns,
  onChange,
  isDisabled = false,
  emptyMessage = 'Select media above to load campaigns',
}) => {
  const [pageSize, setPageSize] = useState(10);
  const [displayCount, setDisplayCount] = useState(10);

  // Group campaigns by their group
  const groupedCampaigns = useMemo(() => {
    const groups: Record<string, { groupName: string; campaigns: Campaign[] }> =
      {};

    campaigns.forEach((campaign) => {
      const groupKey = campaign.groupId || 'uncategorized';
      const groupName = campaign.groupName || 'Uncategorized';

      if (!groups[groupKey]) {
        groups[groupKey] = { groupName, campaigns: [] };
      }
      groups[groupKey].campaigns.push(campaign);
    });

    // Sort: named groups first (alphabetically), then uncategorized
    return Object.entries(groups).sort(([keyA, a], [keyB, b]) => {
      if (keyA === 'uncategorized') return 1;
      if (keyB === 'uncategorized') return -1;
      return a.groupName.localeCompare(b.groupName);
    });
  }, [campaigns]);

  // Flatten campaigns for display with pagination
  const flatCampaignsWithGroups = useMemo(() => {
    const result: Array<
      | { type: 'group'; groupKey: string; groupName: string; count: number }
      | { type: 'campaign'; campaign: Campaign }
    > = [];

    groupedCampaigns.forEach(([groupKey, group]) => {
      result.push({
        type: 'group',
        groupKey,
        groupName: group.groupName,
        count: group.campaigns.length,
      });
      group.campaigns.forEach((campaign) => {
        result.push({ type: 'campaign', campaign });
      });
    });

    return result;
  }, [groupedCampaigns]);

  // Paginated items
  const displayedItems = useMemo(() => {
    return flatCampaignsWithGroups.slice(0, displayCount);
  }, [flatCampaignsWithGroups, displayCount]);

  const totalCampaigns = campaigns.length;
  const hasMore = displayCount < flatCampaignsWithGroups.length;

  // Selection state
  const isAllSelected = useMemo(() => {
    if (campaigns.length === 0) return false;
    return campaigns.every((c) => selectedCampaigns[c.campaignId] === true);
  }, [campaigns, selectedCampaigns]);

  const handleSelectAll = useCallback(() => {
    if (isDisabled || campaigns.length === 0) return;
    const newSelections: Record<string, boolean> = {};
    const newValue = !isAllSelected;
    campaigns.forEach((c) => {
      newSelections[c.campaignId] = newValue;
    });
    onChange(newSelections);
  }, [isDisabled, isAllSelected, campaigns, onChange]);

  const handleToggleCampaign = useCallback(
    (campaignId: string) => {
      if (isDisabled) return;
      const newSelections = {
        ...selectedCampaigns,
        [campaignId]: !selectedCampaigns[campaignId],
      };
      onChange(newSelections);
    },
    [isDisabled, selectedCampaigns, onChange],
  );

  const handleShowMore = useCallback(() => {
    setDisplayCount((prev) =>
      Math.min(prev + pageSize, flatCampaignsWithGroups.length),
    );
  }, [pageSize, flatCampaignsWithGroups.length]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setDisplayCount(newSize);
  }, []);

  return (
    <Box
      border="1px solid"
      borderColor="border.layout"
      borderRadius="24px"
      p={6}
      bg="surface.card">
      <Text fontSize="16px" fontWeight={600} color="text.title" mb={6}>
        SELECT CAMPAIGNS FOR CATEGORY LEVEL MODELING
      </Text>

      <Box
        position="relative"
        border="1px solid"
        borderColor="border.layout"
        borderRadius="md"
        maxH="500px"
        overflowY="auto">
        {/* Header row */}
        <HStack
          position="sticky"
          top={0}
          zIndex={1}
          px={4}
          py={4}
          justify="space-between"
          bg="surface.hover"
          borderBottom="1px solid"
          borderColor="border.layout">
          <Text fontSize="14px" fontWeight={600} color="darkGray">
            Campaign Name
          </Text>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor={
              isDisabled || campaigns.length === 0 ? 'not-allowed' : 'pointer'
            }
            onClick={handleSelectAll}
            w="18px"
            h="18px"
            bg="transparent"
            border="2px solid"
            borderColor={isAllSelected ? 'icon.green' : 'gray.300'}
            color={isAllSelected ? 'icon.green' : 'gray.300'}
            borderRadius="full"
            _hover={{ opacity: isDisabled ? 1 : 0.8 }}>
            {isAllSelected && <Icon iconName="check" width={2} height={2} />}
          </Box>
        </HStack>

        {/* Campaign list */}
        <VStack spacing={0} align="stretch">
          {campaigns.length === 0 ? (
            <Box px={4} py={4}>
              <Text fontSize="16px" color="gray.500">
                {emptyMessage}
              </Text>
            </Box>
          ) : (
            displayedItems.map((item) => {
              if (item.type === 'group') {
                return (
                  <Box
                    key={`group-${item.groupKey}`}
                    px={4}
                    py={3}
                    borderBottom="1px solid"
                    borderColor="border.layout">
                    <Text fontSize="14px" color="darkGray">
                      Category:{' '}
                      <Text as="span" color="icon.green" fontWeight={600}>
                        {item.groupName}
                      </Text>{' '}
                      <Text as="span" color="darkGray">
                        {item.count}
                      </Text>
                    </Text>
                  </Box>
                );
              }

              const { campaign } = item;
              const isSelected =
                selectedCampaigns[campaign.campaignId] === true;

              return (
                <HStack
                  key={campaign.campaignId}
                  px={4}
                  py={4}
                  justify="space-between"
                  borderBottom="1px solid"
                  borderColor="border.layout"
                  _hover={{ bg: isDisabled ? 'transparent' : 'surface.hover' }}
                  cursor={isDisabled ? 'not-allowed' : 'pointer'}
                  onClick={() => handleToggleCampaign(campaign.campaignId)}>
                  <Text
                    fontSize="16px"
                    fontWeight={500}
                    color="text.title"
                    noOfLines={1}>
                    {campaign.campaignName || campaign.campaignId}
                  </Text>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="18px"
                    h="18px"
                    bg="transparent"
                    border="2px solid"
                    borderColor={isSelected ? 'icon.green' : 'gray.300'}
                    color={isSelected ? 'icon.green' : 'gray.300'}
                    borderRadius="full"
                    _hover={{ opacity: isDisabled ? 1 : 0.8 }}>
                    {isSelected && (
                      <Icon iconName="check" width={2} height={2} />
                    )}
                  </Box>
                </HStack>
              );
            })
          )}
        </VStack>

        {/* Pagination */}
        {campaigns.length > 0 && (
          <Box position="sticky" bottom={3} width="fit-content" mx="auto">
            <PaginationControls
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              totalItems={totalCampaigns}
              displayedCount={displayedItems.length}
              onShowMore={handleShowMore}
              hasMore={hasMore}
              itemLabel="Results"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
